import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, ShieldCheck, ClipboardList, Users, Sparkles, MessageCircle } from "lucide-react";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import { ReviewsSection } from "../components/ReviewsSection";
import { ServiceCard } from "../components/ServiceCard";
import { QuoteForm } from "../components/QuoteForm";
import { COMPANY, SERVICES, PROJECTS, IMAGES, waLink } from "../lib/constants";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-[92vh] items-center overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Modern British home exterior renovated by a professional building team" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-green/95 via-brand-green/75 to-brand-green/25" />
      </motion.div>
      <div className="relative mx-auto w-full max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="text-xs font-bold uppercase tracking-[0.28em] text-white/85"
        >
          KAC — Krishnaa Constructions · {COMPANY.tagline}
        </motion.p>
        <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-[64px]">
          <MaskedLine delay={0.3}>Building Your Vision.</MaskedLine>
          <MaskedLine delay={0.45}>
            Creating <span className="text-white underline decoration-brand-red decoration-[6px] underline-offset-8">Better Spaces.</span>
          </MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg"
        >
          From loft conversions and house extensions to electrical, plumbing, kitchen and bathroom renovations, KAC provides
          building services to help transform your property.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/contact"
            data-testid="hero-quote-btn"
            className="rounded-full bg-brand-red px-8 py-4 text-base font-bold text-white shadow-xl shadow-brand-red/30 transition-all hover:bg-brand-redDark hover:shadow-2xl active:scale-95"
          >
            Get a Free Quote
          </Link>
          <Link
            to="/services"
            data-testid="hero-services-btn"
            className="rounded-full border-2 border-white/80 px-8 py-[14px] text-base font-bold text-white backdrop-blur-sm transition-all hover:bg-white hover:text-brand-green active:scale-95"
          >
            Explore Our Services
          </Link>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-3 text-sm font-semibold text-white/80"
        >
          {["Free, no-obligation written quotes", "All types of building work", COMPANY.areas].map((t) => (
            <span key={t} className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-red" /> {t}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Intro = () => (
  <section data-testid="intro-section" className="py-20 md:py-28">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
      <Reveal>
        <div className="relative">
          <div className="absolute -left-4 -top-4 h-24 w-24 rounded-2xl border-2 border-brand-red/25" aria-hidden="true" />
          <img src={IMAGES.extension} alt="Rear house extension with brickwork and large doors, illustrative" className="relative aspect-[4/3] w-full rounded-2xl object-cover shadow-xl" />
          <div className="absolute -bottom-6 -right-4 rounded-2xl bg-brand-green px-6 py-4 text-white shadow-xl sm:-right-6">
            <p className="font-heading text-2xl font-extrabold">KAC</p>
            <p className="text-xs font-semibold text-white/80">{COMPANY.tagline}</p>
          </div>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-green2">Welcome to KAC</p>
        <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-brand-green sm:text-4xl">
          One trusted team for every part of your build.
        </h2>
        <p className="mt-5 leading-relaxed text-brand-muted">
          Krishnaa Constructions is a UK building company run by {COMPANY.contact}, carrying out all types of building work —
          from loft conversions and extensions to kitchens, bathrooms, electrical, plumbing and tiling.
        </p>
        <p className="mt-4 leading-relaxed text-brand-muted">
          Because every trade is handled by one team, your project runs smoothly: one point of contact, one clear written
          quote, and workmanship we stand behind from the first sketch to the final clean-down.
        </p>
        <Link to="/about" data-testid="intro-about-link" className="mt-7 inline-flex items-center gap-2 font-bold text-brand-red transition-colors hover:text-brand-redDark">
          More about us <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ServicesSection = () => (
  <section data-testid="home-services" className="bg-brand-light/60 py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-green2">What we do</p>
        <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-brand-green sm:text-4xl">Our Services</h2>
        <p className="mt-4 text-brand-muted">
          Every service below is carried out by our own team. Tap any card to request a free, no-obligation quote.
        </p>
      </Reveal>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.id} service={s} index={i} />
        ))}
      </div>
    </div>
  </section>
);

const WHY = [
  { icon: Users, title: "One team, every trade", text: "Building, electrics, plumbing and tiling coordinated together — no chasing multiple contractors." },
  { icon: ClipboardList, title: "Clear written quotes", text: "You receive a detailed, itemised quote before any work begins. No surprises, no pressure." },
  { icon: ShieldCheck, title: "Workmanship we stand behind", text: "Careful preparation, tidy sites and finishes checked properly before we call a job done." },
  { icon: MessageCircle, title: "Straightforward communication", text: `You deal directly with ${COMPANY.contact} from first call to final handover — by phone or WhatsApp.` },
];

const WhyUs = () => (
  <section data-testid="why-choose-us" className="py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-green2">Why choose us</p>
        <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-brand-green sm:text-4xl">
          Built on trust, finished with care.
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.08}>
            <div className="h-full rounded-2xl border-l-4 border-brand-red bg-white p-7 shadow-sm transition-shadow hover:shadow-lg">
              <w.icon className="h-7 w-7 text-brand-green" strokeWidth={1.8} />
              <h3 className="mt-4 font-heading text-lg font-bold text-brand-green">{w.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-muted">{w.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FeaturedProjects = () => {
  const [genuine, setGenuine] = useState([]);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects`).then((r) => setGenuine(r.data)).catch(() => {});
  }, []);
  const hasGenuine = genuine.length > 0;
  const list = hasGenuine ? genuine.slice(0, 3) : PROJECTS.slice(0, 3);

  return (
  <section data-testid="featured-projects" className="bg-brand-light/60 py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div className="max-w-xl">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-green2">Our work</p>
          <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-brand-green sm:text-4xl">Featured Projects</h2>
        </div>
        <Link to="/projects" data-testid="featured-view-all" className="inline-flex items-center gap-2 font-bold text-brand-red transition-colors hover:text-brand-redDark">
          View all projects <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {list.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <Link to="/projects" data-testid={`featured-project-${p.id}`} className="group block overflow-hidden rounded-2xl bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={hasGenuine ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.image_path}` : p.image}
                  alt={hasGenuine ? `${p.title} — genuine KAC project` : `${p.title} — illustrative project visual`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-full bg-brand-green px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">{p.category}</span>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-lg font-bold text-brand-green">{p.title}</h3>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-brand-muted">
                  {hasGenuine ? `${p.location ? `${p.location} · ` : ""}Genuine KAC work` : `${p.location} · Illustrative image`}
                </p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
      {!hasGenuine && (
        <p className="mt-6 text-xs text-brand-muted">Images shown are illustrative placeholders — genuine photographs of completed KAC projects will be added soon.</p>
      )}
    </div>
  </section>
  );
};

const STEPS = [
  { n: "01", title: "Get in touch", text: "Call, WhatsApp or send the quote form. Tell us what you're planning." },
  { n: "02", title: "Site visit & quote", text: `${COMPANY.contact} visits your property, discusses options and provides a clear written quote.` },
  { n: "03", title: "We build", text: "Agreed start date, tidy site, regular updates while the work is carried out." },
  { n: "04", title: "Handover", text: "Final walk-through together, everything checked and finished to the agreed standard." },
];

const Process = () => (
  <section data-testid="process-section" className="py-20 md:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal className="max-w-2xl">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-brand-green2">How it works</p>
        <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-brand-green sm:text-4xl">
          From first call to final finish.
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.08}>
            <div className="relative h-full rounded-2xl border border-brand-light bg-white p-7 transition-shadow hover:shadow-lg">
              <span className="font-heading text-4xl font-extrabold text-brand-red/90">{s.n}</span>
              <h3 className="mt-3 font-heading text-lg font-bold text-brand-green">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-brand-muted">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Enquiry = () => (
  <section data-testid="home-enquiry" className="relative overflow-hidden bg-brand-green py-20 md:py-28">
    <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/5" aria-hidden="true" />
    <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-brand-red/15" aria-hidden="true" />
    <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
      <Reveal className="lg:col-span-2">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-white/70">Free quote</p>
        <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
          Tell us about your project.
        </h2>
        <p className="mt-5 leading-relaxed text-white/80">
          Send the form and {COMPANY.contact} will get back to you to arrange a free, no-obligation quote. Prefer to talk
          right now? Call or WhatsApp — we're happy to help.
        </p>
        <div className="mt-8 space-y-4">
          <a href={`tel:${COMPANY.phone}`} data-testid="enquiry-call" className="flex items-center gap-4 rounded-2xl bg-white/10 p-5 text-white backdrop-blur-sm transition-colors hover:bg-white/15">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-red"><Phone className="h-5 w-5" /></span>
            <span>
              <span className="block text-xs uppercase tracking-widest text-white/60">Call us</span>
              <span className="font-heading text-lg font-bold">{COMPANY.phoneDisplay}</span>
            </span>
          </a>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="enquiry-whatsapp" className="flex items-center gap-4 rounded-2xl bg-white/10 p-5 text-white backdrop-blur-sm transition-colors hover:bg-white/15">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366]"><MessageCircle className="h-5 w-5" /></span>
            <span>
              <span className="block text-xs uppercase tracking-widest text-white/60">WhatsApp</span>
              <span className="font-heading text-lg font-bold">Message {COMPANY.contact}</span>
            </span>
          </a>
        </div>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-3">
        <div className="rounded-3xl bg-white p-7 shadow-2xl sm:p-10">
          <QuoteForm />
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  useEffect(() => {
    document.title = "KAC — Krishnaa Constructions | All Types of Building Work, UK";
  }, []);
  return (
    <>
      <Hero />
      <Marquee />
      <Intro />
      <ServicesSection />
      <WhyUs />
      <ReviewsSection />
      <FeaturedProjects />
      <Process />
      <Enquiry />
    </>
  );
}
